import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirmEmail } = useAuth();

  const [verificationState, setVerificationState] = useState<'verifying' | 'success' | 'error' | 'invalid'>("verifying");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [verificationAttempted, setVerificationAttempted] = useState<boolean>(false);

  useEffect(() => {
    const verifyEmailToken = async () => {
      if (verificationAttempted) {
        return;
      }

      const uri = searchParams.get("signedUrl");

      if (!uri) {
        setVerificationState("invalid");
        setErrorMessage("Érvénytelen ellenőrzési link. Hiányzó token.");
        return;
      }

      try {
        setVerificationAttempted(true);
        const success = await confirmEmail(uri);

        if (success) {
          setVerificationState("success");
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        } else {
          setVerificationState("error");
        }
      } catch (error) {
        console.error("Email verification error:", error);
        setVerificationState("error");
        setErrorMessage("A megerősítés során hiba történt. Kérjük, próbálja újra később.");
      }
    };

    verifyEmailToken();
  }, [searchParams, confirmEmail, navigate, verificationAttempted]);

  const renderContent = () => {
    switch (verificationState) {
      case "verifying":
        return (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">E-mail cím megerősítése</CardTitle>
              <CardDescription className="text-center">
                Kérjük, várjon amíg megerősítjük e-mail címét...
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-16 w-16 text-medihub-primary animate-spin" />
              <p className="mt-4 text-center text-muted-foreground">
                Ez néhány másodpercet vehet igénybe.
              </p>
            </CardContent>
          </>
        );

      case "success":
        return (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center text-green-600">Sikeres megerősítés</CardTitle>
              <CardDescription className="text-center">
                E-mail címét sikeresen megerősítettük.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500" />
              <p className="mt-4 text-center">
                Fiókja aktiválva. Átirányítjuk a bejelentkezési oldalra...
              </p>
            </CardContent>
          </>
        );

      case "error":
        return (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center text-red-600">Sikertelen megerősítés</CardTitle>
              <CardDescription className="text-center">
                Nem sikerült megerősíteni az e-mail címét.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <XCircle className="h-16 w-16 text-red-500" />
              <p className="mt-4 text-center text-muted-foreground">
                {errorMessage || "A megerősítő link érvénytelen vagy lejárt."}
              </p>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button asChild className="w-full">
                <Link to="/login">Vissza a bejelentkezéshez</Link>
              </Button>
            </CardFooter>
          </>
        );

      case "invalid":
        return (
          <>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center text-amber-600">Érvénytelen kérés</CardTitle>
              <CardDescription className="text-center">
                Hiányzó vagy érvénytelen megerősítési információk.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <AlertTriangle className="h-16 w-16 text-amber-500" />
              <p className="mt-4 text-center text-muted-foreground">
                {errorMessage || "A megadott megerősítési linkben hiányoznak a szükséges információk."}
              </p>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button asChild className="w-full">
                <Link to="/login">Vissza a bejelentkezéshez</Link>
              </Button>
            </CardFooter>
          </>
        );
    }
  };

  return (
    <div className="container mx-auto flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        {renderContent()}
      </Card>
    </div>
  );
};

export default VerifyEmail;
